import Layout from '../Layout';
import React, {
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
} from 'react';
import IPOdetails from '../../common/components/IPODetails';
import Head from 'next/head';
import {
  getMetaTitle,
  getMetaDescription,
} from '../../common/components/IPODetails/constants';
import useCurrentWidth from '../../common/hooks/useCurrentWidth';
import mixpanel from '../../services/mixpanel';
import { EXCLUDED_LIC_IPO_IDS } from '../../components/Models/Actions/Footer/url-helper';
import ActionButtons from '../ActionButtons';
const ipoDetailsBaseUrl = `${process.env.BASE_URL}/ipo-initial-public-offering`;

interface Props {
  ipo: {
    name: string;
    seoName: string;
    ipoId: string;
    displayText2?: {
      seoImages?: {
        banner?: {
          url?: string;
          alt?: string;
          title?: string;
        };
      };
    };
    seoMeta: {
      title: string;
      desc: string;
      canonicalSlug: string;
    };
    brandName: string;
  };
  seo: boolean;
  queryParam: string;
  queryType: string;
}

export const breadcrumbSchema = (data: any) => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': `${process.env.BASE_URL}`,
          name: 'Trading',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': ipoDetailsBaseUrl,
          name: 'IPO',
        },
      },
      data,
    ],
  });
};

const IPOPage: FunctionComponent<Props> = ({
  ipo,
  queryParam,
  queryType,
  seo = false,
}) => {
  const isServer = typeof window == 'undefined';
  const [showForm, setShowForm] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth <= 768);
    }
  }, []);

  useEffect(() => {
    if (typeof window != 'undefined') {
      mixpanel.track('IPO Details Page Visited', {
        [queryType]: queryParam,
        ipoName: ipo?.name,
      });
    }
  }, [ipo?.name, queryParam]);

  const handleClick = useCallback(() => {
    setShowForm(!showForm);
  }, [showForm]);

  const screenWidth = useCurrentWidth();

  const ipoUrl =
    queryType === 'seoName'
      ? `/${ipo.seoName}`
      : `/${ipo.seoName}/${ipo.ipoId}`;
  const canonicalSlug = ipo?.seoMeta?.canonicalSlug ? `/${ipo?.seoMeta?.canonicalSlug}`: "";
  const breadcrumbItem = {
    '@type': 'ListItem',
    position: 3,
    item: {
      '@id': `${ipoDetailsBaseUrl}${ipoUrl}`,
      name: ipo?.name,
    },
  };
  const getIpoBannerImage = () =>
    ipo?.displayText2?.seoImages?.banner?.url && (
      <div>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className="banner-image"
          src={ipo?.displayText2?.seoImages?.banner?.url}
          alt={ipo?.displayText2?.seoImages?.banner?.alt}
        />
      </div>
    );
  
    return (
    <>
      {seo && !EXCLUDED_LIC_IPO_IDS.includes(ipo?.ipoId) ? (
        <div>
          <Head>
            <meta name="robots" content="all" />
          </Head>
        </div>
      ) : (
        <div>
          <Head>
            <meta name="robots" content="noindex,nofollow" />
          </Head>
        </div>
      )}
      {queryParam ? (
        <div>
          <Layout
            pageTitle={
              ipo?.seoMeta?.title
                || getMetaTitle(
                    ipo.brandName && ipo.brandName.length > 0
                      ? ipo.brandName
                      : ipo.name,
                      ipo.name,
                  )
            }
            showSidebar={!screenWidth || screenWidth > 992}
            pageHeader={getIpoBannerImage()}
            // allowFooterBtmSpacing={screenWidth > 0 && screenWidth <= 992}
          >
            <Head>
              <meta
                name="description"
                content={
                  ipo?.seoMeta?.desc
                    || getMetaDescription(
                        ipo.brandName && ipo.brandName.length > 0
                          ? ipo.brandName
                          : ipo.name,
                        ipo.name,
                      )
                }
              />
              <link rel="canonical" href={`${ipoDetailsBaseUrl}${canonicalSlug || ipoUrl}`} />
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: breadcrumbSchema(breadcrumbItem),
                }}
              ></script>
            </Head>
            <IPOdetails
              applyOnClick={handleClick}
              queryParam={queryParam}
              ipoDetails={ipo}
              showFullCompanyDetails={isServer}
              baseUrl={ipoDetailsBaseUrl}
              ipoUrl={`${ipoDetailsBaseUrl}${ipoUrl}`}
            />
          </Layout>
          {isMobile && <ActionButtons />}
        </div>
      ) : null}
    </>
  );
};

export default IPOPage;
